import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { compact, isEmpty } from 'lodash';

import type { DatabaseEntities, Maybe, YardTaskAttachment } from '@nova/core';
import { documentKeys } from '@satellite/composables/queryKeys';
import documentService from '@satellite/services/document-service';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Read by AxiosPlugin's interceptors: skips the success/error toast for the request. */
    suppressNotification?: boolean;
  }
}

type UploadAttachmentVariables = {
  relatedEntity: DatabaseEntities;
  relatedId: string;
  file: File;
  /** Silences the AxiosPlugin interceptor, for a caller that reports the failure itself. */
  suppressNotification?: boolean;
};

export interface UseAttachmentUploadOptions {
  /** Which entity the documents hang off, e.g. `DatabaseEntities.YardTask`. */
  relatedEntity: MaybeRefOrGetter<DatabaseEntities>;
  /** The entity's id. Nothing is uploaded until there is one. */
  relatedId: MaybeRefOrGetter<Maybe<string>>;
  /** Runs once an upload succeeds, before the list is invalidated. Once per file. */
  onSuccess?: (attachment: YardTaskAttachment) => void;
  /** Runs when an upload fails, once per file. `uploadAttachments` still resolves. */
  onError?: (error: unknown) => void;
}

/**
 * Adds documents to an entity's attachments and invalidates the list they belong to, so every
 * page comes back from the API rather than being patched locally. Accepts any file: which of
 * them an app is willing to attach is `getAttachmentRejection`'s call, before this is reached.
 * Pairs with `useAttachmentList` and `useAttachmentDelete`.
 */
export function useAttachmentUpload({
  relatedEntity,
  relatedId,
  onSuccess,
  onError
}: UseAttachmentUploadOptions) {
  const queryClient = useQueryClient();

  /**
   * Held across a whole batch: the mutation settles between files, and reading its pending flag
   * alone would flicker the caller's `isUploading` once per file.
   */
  const isBatchPending = ref(false);

  const mutation = useMutation<YardTaskAttachment, unknown, UploadAttachmentVariables>({
    // The request config is passed only when silencing, so the ordinary upload reaches the
    // service exactly as it did before there was a staged one.
    mutationFn: ({ relatedEntity: entity, relatedId: id, file, suppressNotification }) =>
      suppressNotification
        ? documentService.uploadDocument(entity, id, file, {
            requestConfig: { suppressNotification: true }
          })
        : documentService.uploadDocument(entity, id, file),
    // Wrapped so the caller is handed the attachment alone, not the mutation's own arguments.
    // The list is invalidated once a batch settles rather than here, so a pick of ten files is
    // one refetch instead of ten.
    onSuccess: attachment => onSuccess?.(attachment),
    onError
  });

  /** Never rejects: failures reach the caller through `onError`. */
  async function upload(variables: UploadAttachmentVariables): Promise<boolean> {
    try {
      await mutation.mutateAsync(variables);
      return true;
    } catch {
      // Reported by onError.
      return false;
    }
  }

  /**
   * Never rejects, and resolves with how many files were stored.
   *
   * Files go up one at a time rather than all at once: a pick can be ten of them, and the API
   * takes one per request. The list is invalidated once, after the last, so a batch settles into
   * a single refetch — and only when something was actually stored, so a batch that failed
   * outright never shows a tile that is not there.
   *
   * Both the entity and its id are read here rather than on the way back, so a list that moved
   * on while the requests were in flight is not the one invalidated.
   */
  async function uploadAttachments(files: Maybe<Array<Maybe<File>>>): Promise<number> {
    const currentEntity = toValue(relatedEntity);
    const currentId = toValue(relatedId);
    const currentFiles = compact(files ?? []);

    if (!currentId || isEmpty(currentFiles)) {
      return 0;
    }

    isBatchPending.value = true;

    try {
      let storedCount = 0;

      for (const file of currentFiles) {
        // Sequential on purpose: a pick of ten files in parallel is ten multipart uploads at once.
        // eslint-disable-next-line no-await-in-loop
        const stored = await upload({
          relatedEntity: currentEntity,
          relatedId: currentId,
          file
        });

        storedCount += stored ? 1 : 0;
      }

      if (storedCount > 0) {
        await queryClient.invalidateQueries({
          queryKey: documentKeys.list(currentEntity, currentId)
        });
      }

      return storedCount;
    } finally {
      isBatchPending.value = false;
    }
  }

  /**
   * Uploads files that were held client-side while the entity did not exist yet, against the id
   * it was given once created. `entityId` is an argument rather than the reactive `relatedId`,
   * which is still empty at the moment a create form has something to attach to.
   *
   * Sequential on purpose: a form carries a handful of files, and one failure should not be
   * buried under five more in flight. Never rejects — what failed comes back for the caller to
   * report, so a created entity is not made to look like a failed one.
   *
   * Pairs with `useStagedAttachments`, which holds the files until this is called.
   */
  async function uploadStagedAttachments(
    entityId: string,
    files: File[]
  ): Promise<Array<{ file: File; error: unknown }>> {
    const currentEntity = toValue(relatedEntity);
    const failures: Array<{ file: File; error: unknown }> = [];

    for (const file of files) {
      try {
        // Sequential on purpose: `Promise.all` here is what buries one failure under the rest.
        // eslint-disable-next-line no-await-in-loop
        await mutation.mutateAsync({
          relatedEntity: currentEntity,
          relatedId: entityId,
          file,
          // The caller reports these together, by count; the interceptor would add one
          // generic toast per file on top of that.
          suppressNotification: true
        });
      } catch (error) {
        failures.push({ file, error });
      }
    }

    // Once, after the last file, and only when something was stored — the same rule the picked
    // batch follows, since the mutation itself no longer invalidates per file.
    if (failures.length < files.length) {
      await queryClient.invalidateQueries({
        queryKey: documentKeys.list(currentEntity, entityId)
      });
    }

    return failures;
  }

  return {
    uploadAttachments,
    uploadStagedAttachments,
    isUploading: computed(() => isBatchPending.value || mutation.isPending.value)
  };
}
